import {WalletPlatform} from '@/enums/WalletPlatform'
import {
  RejectedByUser,
  SdkNotInitializedError,
} from '@/model/wallets/types/WalletErrors'
import {ConnectionStrategy} from './ConnectionStrategy'
import {nep21Registry} from './NEP21Registry'
import {
  registerNep21ContextListeners,
  NeolineContextChangeHandler,
} from './NeolineContextListeners'

/**
 * Connected-provider context for OneGate's in-app dApp browser.
 *
 * OneGate (wallet v2.0+) is NEP-21 compliant: inside its in-app dApp browser it
 * announces a unified dAPI provider through the `Neo.DapiProvider.ready` event
 * with `name: "OneGate"`. We connect to that provider directly — OneGate has no
 * desktop extension nor a synchronously injected global like NeoLine's
 * `window.NeoLineN3Provider`, so detection happens through the NEP-21 registry.
 */
export type OneGateNep21ProviderContext = {
  type: 'onegate-nep21'
  provider: any
  address: string | null
}

export const isOneGateNep21Provider = (
  provider: unknown
): provider is OneGateNep21ProviderContext =>
  typeof provider === 'object' &&
  provider !== null &&
  (provider as OneGateNep21ProviderContext).type === 'onegate-nep21'

/** Name OneGate announces itself with through the NEP-21 discovery event. */
const ONE_GATE_PROVIDER_NAME = 'OneGate'

/** Ask any NEP-21 wallet present to (re)announce itself via `Neo.DapiProvider.ready`. */
const requestOneGateProviderAnnouncement = () => {
  if (typeof window === 'undefined') return

  window.dispatchEvent(
    new CustomEvent('Neo.DapiProvider.request', {
      detail: {type: 'NEP-21'},
    })
  )
}

/** Resolve the OneGate dAPI provider from the NEP-21 registry (in-app browser only). */
const resolveOneGateProvider = (): any => {
  if (typeof window === 'undefined') return null

  return nep21Registry.get(ONE_GATE_PROVIDER_NAME) ?? null
}

/**
 * True when running inside the OneGate in-app dApp browser, i.e. the OneGate
 * NEP-21 provider has announced itself. Triggers a discovery request first so a
 * provider that has not announced yet gets a chance to before the next check.
 */
export const isOneGateInAppBrowser = (): boolean => {
  if (typeof window === 'undefined') return false

  nep21Registry.init()
  requestOneGateProviderAnnouncement()

  return !!resolveOneGateProvider()
}

/** Read the active address without prompting (used to restore an existing session). */
const readAnnouncedAddress = async (provider: any): Promise<string | null> => {
  if (provider?.account?.address) {
    return provider.account.address
  }

  if (typeof provider?.getAccounts === 'function') {
    const accounts = await provider.getAccounts()
    const first = Array.isArray(accounts) ? accounts[0] : accounts

    return first?.address ?? null
  }

  return null
}

/** Prompt OneGate to authorize the dApp and return the connected address. */
const requestConnection = async (provider: any): Promise<string | null> => {
  try {
    if (typeof provider.getAccount === 'function') {
      const account = await provider.getAccount()

      return account?.address ?? null
    }

    if (typeof provider.getAccounts === 'function') {
      const accounts = await provider.getAccounts()
      const first = Array.isArray(accounts) ? accounts[0] : accounts

      return first?.address ?? null
    }
  } catch (error) {
    if (
      error?.type === 'CONNECTION_DENIED' ||
      error?.code === 4001 ||
      error === RejectedByUser
    ) {
      throw RejectedByUser
    }

    throw error
  }

  return readAnnouncedAddress(provider)
}

/** Wait for the OneGate provider to announce itself, up to `timeoutMs`. */
const waitForProvider = (timeoutMs: number): Promise<any | null> => {
  const existing = resolveOneGateProvider()

  if (existing) {
    return Promise.resolve(existing)
  }

  requestOneGateProviderAnnouncement()

  return new Promise(resolve => {
    const done = (provider: any | null) => {
      clearTimeout(timeoutId)
      clearInterval(pollId)
      window.removeEventListener('Neo.DapiProvider.ready', onReady)
      resolve(provider)
    }

    const timeoutId = setTimeout(() => done(null), timeoutMs)

    const onReady = (event: any) => {
      const provider = event?.detail?.provider

      if (
        provider?.name?.toLowerCase() === ONE_GATE_PROVIDER_NAME.toLowerCase()
      ) {
        done(nep21Registry.get(ONE_GATE_PROVIDER_NAME) ?? provider)
      }
    }

    window.addEventListener('Neo.DapiProvider.ready', onReady)

    const pollId = setInterval(() => {
      const provider = resolveOneGateProvider()

      if (provider) {
        done(provider)
      }
    }, 200)
  })
}

export class OneGateConnection implements ConnectionStrategy {
  private provider: any = null
  private address: string | null = null
  private onDeleteAdapter?: () => void
  private detachContextListeners?: () => void

  async init(onDeleteAdapter?: () => void) {
    this.detachContextListeners?.()
    this.detachContextListeners = undefined
    this.onDeleteAdapter = onDeleteAdapter

    if (typeof window === 'undefined') {
      return
    }

    nep21Registry.init()
    requestOneGateProviderAnnouncement()

    // OneGate only exists inside its in-app browser. When it isn't present we
    // leave the connection dormant (the modal shows the "coming soon" step)
    // instead of throwing, so the remaining wallets still initialize cleanly.
    const provider = resolveOneGateProvider()

    if (provider) {
      this.attachProvider(provider)
    }
  }

  async restoreSession() {
    if (!this.provider) {
      const provider = resolveOneGateProvider()

      if (provider) {
        this.attachProvider(provider)
      }
    }

    if (!this.provider) {
      this.address = null
      return
    }

    try {
      this.address = await readAnnouncedAddress(this.provider)
    } catch {
      this.address = null
    }
  }

  isInstalled() {
    return !!(this.provider ?? resolveOneGateProvider())
  }

  async connect() {
    const provider =
      this.provider ??
      resolveOneGateProvider() ??
      (await waitForProvider(10000))

    if (!provider) {
      throw SdkNotInitializedError(WalletPlatform.ONEGATE)
    }

    this.attachProvider(provider)

    this.address = await requestConnection(provider)

    if (!this.address) {
      throw RejectedByUser
    }

    return this.getConnectedProvider()
  }

  getConnectedProvider(): OneGateNep21ProviderContext | null {
    if (!this.provider) {
      return null
    }

    return {
      type: 'onegate-nep21',
      provider: this.provider,
      address: this.address,
    }
  }

  async disconnect() {
    this.detachContextListeners?.()
    this.detachContextListeners = undefined

    this.clearCachedAccount()
    this.address = null
  }

  private attachProvider(provider: any) {
    this.provider = provider

    if (this.detachContextListeners) {
      return
    }

    this.detachContextListeners = registerNep21ContextListeners(
      provider,
      this.createContextChangeHandler()
    )
  }

  private createContextChangeHandler(): NeolineContextChangeHandler {
    return () => {
      void this.handleContextChange()
    }
  }

  private async handleContextChange() {
    await this.restoreSession()

    if (!this.address) {
      this.clearCachedAccount()
      this.onDeleteAdapter?.()
    }
  }

  private clearCachedAccount() {
    if (this.provider?.account) {
      this.provider.account = undefined
    }
  }
}
